/**
 * WhatsApp, phase 1 (MKK module 37, D1, 2026-10-24): the mosque's groups, channels and contacts; a message composed from
 * a notice, an event, a banner or free text, previewed as WhatsApp will show it; «Kopier tekst» and «Åpne gruppen»; and
 * the log of what was shared. Behind the `whatsapp` capability (the menu item is absent without it, every route 404s).
 *   whatsapp.send     compose, copy, open, and the log;
 *   whatsapp.manage   «Ny gruppe eller kanal», «Endre», «Arkiver» (two clicks), «Hent tilbake».
 *
 * NOTHING IS SENT FROM HERE. WhatsApp lets no app put a message straight into a group or a channel (and the Business API
 * reaches neither), so the flow is: copy the text (or open WhatsApp with it), open the group, paste and send. A contact
 * is the one target a link can fill: «Åpne chatten» opens it with the text ready. Each copy or open is logged once
 * for that text and target. The phone does the same with the picture through its share sheet (the staff app).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { call, copyText, errSummary, errText, fieldErrors, objectUrl, patch, post, type Body } from '../lib/api';
import { useLoad, useMsg, dropChanged } from '../lib/hooks';
import { osloTime } from '../lib/format';
import { Body as CardBody, Btn, Card, Field, LoadError, Loading, Msg, Tag, TwoStep } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { WhatsAppCore as W, type WaTarget } from '../core';

const EMPTY = { kind: 'group', name: '', inviteUrl: '', note: '', sort: '0' };
type Form = typeof EMPTY;
const toForm = (t: WaTarget): Form => ({ kind: t.kind, name: t.name, inviteUrl: t.inviteUrl, note: t.note || '', sort: String(t.sort || 0) });
const toBody = (f: Form) => ({ kind: f.kind, name: f.name, inviteUrl: f.inviteUrl, note: f.note || null, sort: f.sort === '' ? 0 : Number(f.sort) });
const KIND_TONE: Record<string, 'green' | 'gold' | 'grey'> = { group: 'green', channel: 'gold', contact: 'grey' };
const OPEN_LABEL: Record<string, string> = { group: 'Åpne gruppen', channel: 'Åpne kanalen', contact: 'Åpne chatten' };

/** WhatsApp's own formatting, as the bubble shows it: *fet* and _kursiv_ on one line; everything else as typed. */
export function waFormat(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /\*([^*\n]+)\*|_([^_\n]+)_/g;
  let at = 0, m: RegExpExecArray | null, k = 0;
  while ((m = re.exec(text))) {
    if (m.index > at) out.push(text.slice(at, m.index));
    out.push(m[1] !== undefined ? <b key={k++}>{m[1]}</b> : <i key={k++}>{m[2]}</i>);
    at = m.index + m[0].length;
  }
  if (at < text.length) out.push(text.slice(at));
  return out;
}

/** The message as WhatsApp will show it: a light green bubble, the picture above the text. */
export function Bubble({ text, picture }: { text: string; picture?: string | null }) {
  return (
    <div className="wa-chat" aria-label="Forhåndsvisning slik meldingen ser ut i WhatsApp">
      <div className="wa-bubble">
        {picture && <img src={picture} alt="Bildet som deles med meldingen" />}
        <div className="wa-text">{text ? waFormat(text) : <span className="sub">Ingen tekst ennå.</span>}</div>
      </div>
    </div>
  );
}

function TargetForm({ f, set, errors }: { f: Form; set: (f: Form) => void; errors: Record<string, string> }) {
  const err = (k: string) => (errors[k] ? <span className="msg err" role="alert">{errors[k]}</span> : null);
  return (
    <>
      <div className="row">
        <Field label="Navn" required className="field half">{(id) => <><input id={id} maxLength={W.MAX.name} autoComplete="off" value={f.name}
          onChange={(e) => set({ ...f, name: e.target.value })} />{err('name')}</>}</Field>
        <Field label="Type" required className="field half">{(id) => <><select id={id} value={f.kind} onChange={(e) => set({ ...f, kind: e.target.value })}>
          {W.KINDS.map((k) => <option key={k} value={k}>{W.KIND_NO[k]}</option>)}</select>{err('kind')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Lenke" required className="field" note="Fra WhatsApp: gruppens invitasjonslenke (chat.whatsapp.com/…), kanalens lenke (whatsapp.com/channel/…) eller wa.me/ og nummeret med landkode.">
          {(id, d) => <><input id={id} aria-describedby={d} type="url" inputMode="url" maxLength={W.MAX.url} autoComplete="off" value={f.inviteUrl}
            onChange={(e) => set({ ...f, inviteUrl: e.target.value })} />{err('inviteUrl')}</>}</Field>
      </div>
      <div className="row">
        <Field label="Notat" className="field half" note="Bare for de ansatte, for eksempel hvem gruppen er for.">{(id, d) => <><input id={id} aria-describedby={d} maxLength={W.MAX.note}
          autoComplete="off" value={f.note} onChange={(e) => set({ ...f, note: e.target.value })} />{err('note')}</>}</Field>
        <Field label="Rekkefølge" className="field half" note="0–999; lavest først.">{(id, d) => <><input id={id} aria-describedby={d} type="number" min={0} max={999}
          value={f.sort} onChange={(e) => set({ ...f, sort: e.target.value })} />{err('sort')}</>}</Field>
      </div>
    </>
  );
}

function Targets({ data, reload, showArchived, onToggleArchived }: { data: Body; reload: () => void; showArchived: boolean; onToggleArchived: () => void }) {
  const [msg, say] = useMsg();
  const [form, setForm] = useState<{ id: string | null; f: Form; start: Form } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const items: WaTarget[] = data.items || [];
  const manage = Boolean(data.can && data.can.manage);
  const open = (id: string | null, f: Form) => { setErrors({}); say(''); setForm({ id, f, start: f }); };
  const save = async () => {
    if (!form || saving) return;
    setSaving(true);
    try {
      const r = form.id ? await patch(`/whatsapp/targets/${form.id}`, toBody(form.f)) : await post('/whatsapp/targets', toBody(form.f));
      if (!r.ok) { setErrors(fieldErrors(r.body)); return say(errSummary(r.body), 'err'); }
      setForm(null); say(form.id ? 'Lagret.' : 'Lagt til.', 'ok'); reload();
    } finally { setSaving(false); }
  };
  const act = async (t: WaTarget, what: 'archive' | 'restore') => {
    const r = await post(`/whatsapp/targets/${t.id}/${what}`);
    say(r.ok ? (what === 'archive' ? `${t.name} er arkivert.` : `${t.name} er hentet tilbake.`) : errText(r.body), r.ok ? 'ok' : 'err');
    reload();
  };
  return (
    <Card title="Grupper og kanaler" hint="Moskeens egne WhatsApp-grupper, kanaler og kontakter. «Åpne» åpner dem i WhatsApp; ingenting sendes herfra.">
      {manage && <div className="work-bar"><span className="sp" /><Btn small={false} onClick={() => open(null, EMPTY)}>+ Ny gruppe eller kanal</Btn></div>}
      {!form && <CardBody><Msg text={msg.text} tone={msg.tone} /></CardBody>}
      <table className="t">
        <thead><tr><th scope="col">Navn</th><th scope="col">Type</th><th scope="col">Lenke</th><th scope="col"><span className="vh">Handling</span></th></tr></thead>
        <tbody>{items.length ? items.map((t) => (
          <tr key={t.id}>
            <td><b>{t.name}</b>{t.archivedAt && <> <Tag tone="grey">Arkivert</Tag></>}{t.note && <div className="sub">{t.note}</div>}</td>
            <td><Tag tone={KIND_TONE[t.kind] || 'grey'}>{W.KIND_NO[t.kind] || t.kind}</Tag></td>
            <td className="wa-link">{t.inviteUrl.replace(/^https:\/\//, '')}</td>
            <td>
              {!t.archivedAt && <a className="btn sm ghost" href={t.inviteUrl} target="_blank" rel="noopener noreferrer" aria-label={`Åpne ${t.name} i WhatsApp`}>Åpne</a>}{' '}
              {manage && (t.archivedAt
                ? <Btn ghost aria-label={`Hent tilbake ${t.name}`} onClick={() => act(t, 'restore')}>Hent tilbake</Btn>
                : <><Btn ghost aria-label={`Endre ${t.name}`} onClick={() => open(t.id, toForm(t))}>Endre</Btn>{' '}
                  <TwoStep label="Arkiver" ariaLabel={`Arkiver ${t.name}`} busyLabel="Arkiverer …"
                    confirmLabel="Klikk igjen: skjules fra listen" onConfirm={() => act(t, 'archive')} /></>)}
            </td>
          </tr>)) : <tr><td colSpan={4}>{manage ? 'Ingen grupper ennå. Legg til den første med «Ny gruppe eller kanal».' : 'Ingen grupper ennå. En administrator legger dem til.'}</td></tr>}</tbody>
      </table>
      {data.archivedCount > 0 && <CardBody><p className="note">{data.archivedCount === 1 ? '1 arkivert' : `${data.archivedCount} arkiverte`}{showArchived ? ' vises nederst.' : ' er skjult.'}{' '}
        <Btn ghost onClick={onToggleArchived}>{showArchived ? 'Skjul dem' : 'Vis dem'}</Btn></p></CardBody>}
      <FormDialog open={Boolean(form)} title={form && form.id ? 'Endre gruppe eller kanal' : 'Ny gruppe eller kanal'}
        dirty={Boolean(form) && JSON.stringify(form!.f) !== JSON.stringify(form!.start)} onClose={() => setForm(null)}>
        {form && <>
          <TargetForm f={form.f} set={(f) => { setErrors(dropChanged(errors, form.f, f)); setForm({ ...form, f }); }} errors={errors} />
          <div className="row"><Btn small={false} disabled={saving} onClick={save}>{saving ? 'Lagrer …' : form.id ? 'Lagre' : 'Legg til'}</Btn></div>
          <Msg text={msg.text} tone={msg.tone} />
        </>}
      </FormDialog>
    </Card>
  );
}

function Compose({ targets, onShared }: { targets: WaTarget[]; onShared: () => void }) {
  const { data: sources, error, reload } = useLoad<Body>('/whatsapp/sources');
  const [source, setSource] = useState('notice');
  const [id, setId] = useState('');
  const [built, setBuilt] = useState<Body | null>(null);
  const [text, setText] = useState('');
  const [picture, setPicture] = useState<string | null>(null);
  const [targetId, setTargetId] = useState('');
  const [logged, setLogged] = useState('');
  const [msg, say] = useMsg();
  const list: Body[] = sources ? (source === 'notice' ? sources.notices : source === 'event' ? sources.events : source === 'banner' ? sources.banners : []) || [] : [];
  const target = targets.find((t) => t.id === targetId) || null;

  // A new source: the text from the server (core's composer), the picture behind the session.
  useEffect(() => {
    setBuilt(null); setPicture(null); say('');
    if (source === 'free') { setText(''); return; }
    if (!id) { setText(''); return; }
    let live = true, made: string | null = null;
    void (async () => {
      try {
        const c = await call<Body>(`/whatsapp/compose?source=${source}&id=${encodeURIComponent(id)}`);
        if (!live) return;
        setBuilt(c); setText(c.text);
        if (c.picture) { made = await objectUrl(String(c.picture.path).replace(/^\/v1\/admin/, '')); if (live) setPicture(made); }
      } catch (e) { if (live) say((e as Error).message, 'err'); }
    })();
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [source, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const over = text.length > W.MAX.text;
  const key = `${source}|${id}|${targetId}|${text}`;
  async function log() {
    if (logged === key || !text.trim() || over) return;
    const r = await post('/whatsapp/shares', { targetId: targetId || null, sourceKind: source, sourceId: source === 'free' ? null : id, text, withPicture: false, device: 'web' });
    if (r.ok) { setLogged(key); onShared(); } else say(errText(r.body), 'err');
  }
  async function copy() {
    const ok = await copyText(text);
    say(ok ? (target && target.kind !== 'contact' ? `Kopiert. Åpne ${target.name}, lim inn og send.` : 'Kopiert. Lim inn i WhatsApp og send.') : 'Teksten kunne ikke kopieres. Merk den og kopier selv.', ok ? 'ok' : 'err');
    if (ok) await log();
  }
  const rule = useMemo(() => W.canPrefillGroup(), []);
  const ready = Boolean(text.trim()) && !over && (source === 'free' || Boolean(built));
  return (
    <Card title="Lag en melding" hint={rule.reason_no}>
      <div className="work-bar">
        <div className="bnr-seg work-seg" role="radiogroup" aria-label="Lag meldingen fra">
          {W.SOURCES.map((k) => <button key={k} type="button" role="radio" aria-checked={source === k} onClick={() => { setSource(k); setId(''); }}>{W.SOURCE_NO[k]}</button>)}
        </div>
      </div>
      <div className="wa-compose">
        <div>
          {source !== 'free' && (error ? <LoadError error={error} retry={reload} /> : !sources ? <Loading /> : (
            <Field label={W.SOURCE_NO[source]} className="field" note={list.length ? undefined : 'Ingenting å dele her nå: bare det som er publisert og offentlig kan deles.'}>
              {(fid, d) => <select id={fid} aria-describedby={d} value={id} onChange={(e) => setId(e.target.value)}>
                <option value="">Velg …</option>
                {list.map((x) => <option key={x.id} value={x.id}>{x.title}{x.when ? ` — ${x.when}` : ''}</option>)}
              </select>}
            </Field>))}
          <Field label={<>Tekst<span className={'count' + (over ? ' over' : '')} aria-live="polite">{text.length} / {W.MAX.text}</span></>}
            note="*Stjerner* gjør tekst fet i WhatsApp, _understrek_ kursiv. Du kan endre teksten før du deler.">
            {(fid, d) => <textarea id={fid} aria-describedby={d} rows={9} value={text} onChange={(e) => setText(e.target.value)} />}
          </Field>
          {built && built.truncated && <p className="field note warn">Teksten er kortet ned til det WhatsApp tar imot.</p>}
          <Field label="Til" className="field" note="Valgfritt. Uten gruppe velger du chatten selv i WhatsApp.">
            {(fid, d) => <select id={fid} aria-describedby={d} value={targetId} onChange={(e) => setTargetId(e.target.value)}>
              <option value="">Velg i WhatsApp</option>
              {targets.map((t) => <option key={t.id} value={t.id}>{W.KIND_NO[t.kind]}: {t.name}</option>)}
            </select>}
          </Field>
          <div className="row">
            <Btn small={false} disabled={!ready} onClick={copy}>Kopier tekst</Btn>
            {target
              ? <a className={'btn ghost' + (ready ? '' : ' disabled')} aria-disabled={!ready || undefined} href={ready ? W.openLink(target, text) || '#' : undefined}
                  target="_blank" rel="noopener noreferrer" onClick={() => { if (ready) void log(); }}>{OPEN_LABEL[target.kind]}</a>
              : <a className={'btn ghost' + (ready ? '' : ' disabled')} aria-disabled={!ready || undefined} href={ready ? W.webLink(text) : undefined}
                  target="_blank" rel="noopener noreferrer" onClick={() => { if (ready) void log(); }}>Åpne i WhatsApp</a>}
          </div>
          <Msg text={msg.text} tone={msg.tone} />
          {logged === key && <p className="field note">Ført i loggen nedenfor.</p>}
        </div>
        <Bubble text={text} picture={picture} />
      </div>
      {picture && <CardBody><p className="note">Bildet legges ved fra telefonen (Administrasjon › WhatsApp i appen) eller lastes ned under Bannere › Del.</p></CardBody>}
    </Card>
  );
}

function Log({ version }: { version: number }) {
  const { data, error, reload } = useLoad<Body>('/whatsapp/shares', [version]);
  const items: Body[] = (data && data.items) || [];
  return (
    <Card title="Delt" hint="Det som er delt i WhatsApp fra telefonen eller herfra: hva, hvor, av hvem og når.">
      {error ? <LoadError error={error} retry={reload} /> : !data ? <Loading /> : (
        <table className="t">
          <thead><tr><th scope="col">Når</th><th scope="col">Hva</th><th scope="col">Til</th><th scope="col">Fra</th><th scope="col">Tekst</th></tr></thead>
          <tbody>{items.length ? items.map((s) => (
            <tr key={s.id}>
              <td>{osloTime(s.sharedAt)}{s.sharedByName && <div className="sub">{s.sharedByName}</div>}</td>
              <td>{W.SOURCE_NO[s.sourceKind] || s.sourceKind}{s.withPicture && <div className="sub">med bilde</div>}</td>
              <td>{s.target ? s.target.name : <span className="sub">valgt i WhatsApp</span>}</td>
              <td>{s.device === 'ios' ? 'iPhone' : s.device === 'android' ? 'Android' : 'Panelet'}</td>
              <td className="wa-log-text">{String(s.text).length > 120 ? String(s.text).slice(0, 119) + '…' : s.text}</td>
            </tr>)) : <tr><td colSpan={5}>Ingenting er delt ennå.</td></tr>}</tbody>
        </table>)}
    </Card>
  );
}

export function WhatsApp() {
  const [all, setAll] = useState(false);
  const { data, error, reload } = useLoad<Body>('/whatsapp/targets' + (all ? '?archived=all' : ''), [all]);
  const [version, setVersion] = useState(0);
  if (error) return <Card title="WhatsApp"><LoadError error={error} retry={reload} /></Card>;
  if (!data) return <Card title="WhatsApp"><Loading /></Card>;
  const live = (data.items as WaTarget[]).filter((t) => !t.archivedAt);
  return (
    <>
      <Compose targets={live} onShared={() => setVersion((v) => v + 1)} />
      <Targets data={data} reload={reload} showArchived={all} onToggleArchived={() => setAll(!all)} />
      <Log version={version} />
    </>
  );
}
